import { AxiosError } from 'axios';
import {
  createContext,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Axios } from '@utils/api/axios-instance.ts';
import {
  TokenExtension,
  TokenExtension as Token
} from '@utils/extensions/token.extension.ts';
import { useLogout } from '@utils/api/users/use-logout.ts';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { useCheckMaintenance } from '@utils/api/admin/use-check-maintenance.ts';
import { ENV } from '@utils/constants/app-constants.ts';
import { Roles } from '@utils/types/shared/base.ts';
import * as Sentry from '@sentry/react';

interface ProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<null>(null);

export function AuthContextProvider(props: ProviderProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { mutate: logout } = useLogout();
  const logoutTriggeredRef = useRef(false);
  const { data: user } = useGetMe({ enabled: !!TokenExtension.get()?.string });
  const { data: organization } = useOrganization();

  // Check maintenance status on app initialization (for on-prem deployments)
  // This triggers an API call that will return 503 if maintenance mode is enabled,
  // which the interceptor below will catch and redirect non-admin users to /maintenance
  useCheckMaintenance();

  // Check if current user is an admin (sysop)
  const isAdmin = TokenExtension.get()?.decoded?.scopes?.includes(Roles.SYSOP);

  useEffect(() => {
    const identified = sessionStorage.getItem('sentryIdentified');

    if (
      import.meta.env.VITE_NODE_ENV === 'production' &&
      ENV === 'production' &&
      user?.result &&
      !identified
    ) {
      // Attach user info to Sentry events
      Sentry.setUser({
        id: user.result.email, // or a stable user_id if you have one
        email: user.result.email,
        username: `${user.result.first_name} ${user.result.last_name}`
      });

      // Helpful tags/context
      if (organization) {
        Sentry.setTags({
          org_id: String(organization.id ?? ''),
          org_name: organization.name ?? ''
        });
        Sentry.setContext('organization', {
          id: organization.id ?? '',
          name: organization.name ?? ''
        });
      }

      // Optionally start Replay only when a real user session exists
      const replay = Sentry.getReplay();
      replay?.start();

      sessionStorage.setItem('sentryIdentified', 'true');
    }
  }, [user, organization]);

  const handleSessionRemove = () => {
    if (!logoutTriggeredRef.current) {
      logoutTriggeredRef.current = true;
      logout({ isManual: false });

      // Stop Replay (optional)
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        const replay = Sentry.getReplay();
        replay?.stop();
        Sentry.setUser(null);
      }
    }
  };

  useLayoutEffect(() => {
    // Api requests configs
    const requestConfigs = Axios.interceptors.request.use(
      request => {
        if (!request.url) return request;
        const token = Token.get()?.string;

        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }

        return request;
      },
      async (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Api response configs
    const responseConfigs = Axios.interceptors.response.use(
      response => {
        return response;
      },
      async (error: AxiosError<{ detail?: string; message?: string }>) => {
        // ✅ FIX: Don't auto-logout on shared pages (they use magic link tokens, not auth)
        const isSharedPage =
          pathname.startsWith('/transcripts/shared/') ||
          pathname.startsWith('/captions/shared/');

        // Handle 503 maintenance mode - redirect non-admin users to maintenance page
        if (
          error.response?.status === 503 &&
          error.response?.data?.detail === 'maintenance'
        ) {
          // Admin users (sysop) bypass maintenance mode
          if (!isAdmin) {
            navigate('/maintenance');
          }
          return Promise.reject(error);
        }

        // Don't treat a failed login attempt as a session expiry —
        // the user isn't authenticated yet, so there is no session to remove.
        const isLoginEndpoint = /\/auth\/login(-mfa)?($|\?)/.test(
          error.config?.url || ''
        );
        if (
          error.response?.status === 401 &&
          !isSharedPage &&
          !isLoginEndpoint
        ) {
          handleSessionRemove();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      Axios.interceptors.request.eject(requestConfigs);
      Axios.interceptors.response.eject(responseConfigs);
    };
  }, [pathname, isAdmin, navigate]);

  return (
    <AuthContext.Provider value={null}>{props.children}</AuthContext.Provider>
  );
}
